import React from 'react';
import _ from 'lodash';
import { Typography, Button, BaseIcon, Spinner, SelectMenu } from '@scale/scaleui';
import TemplateCreationForm from './TemplateCreationForm';
import { Link, useParams } from 'react-router-dom';
import { Routes } from 'react-scale/components/GrowthCenter/urls';
import { getEmailCommsTemplateVersions } from 'react-scale/corp/lib/api/growth';
import { useQuery } from 'react-query';
import { EmailTemplateData } from '@scale/shared-types';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';

const TemplateSetupPage = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const templatesLibraryEnabled = useUnleashFeatureFlag('growth-template-library', {}, false);
  const [currentTemplate, setCurrentTemplate] = React.useState<EmailTemplateData | undefined>(
    undefined,
  );
  const [currentVersion, setCurrentVersion] = React.useState(0);

  const { isFetching: fetchTemplateLoading, data: templateVersions } = useQuery(
    ['get-email-comms-template-versions', templateId],
    () => getEmailCommsTemplateVersions(templateId),
    {
      enabled: !!templateId,
      onSuccess: data => {
        if (data && data.length > 0) {
          const latestTemplate = _.maxBy(data, 'version');
          if (!latestTemplate) {
            return;
          }
          setCurrentTemplate(latestTemplate);
          setCurrentVersion(latestTemplate.version ?? 0);
        }
      },
      refetchOnWindowFocus: false,
    },
  );
  const versionOptions =
    templateVersions?.map(t => ({
      id: t.version ?? 0,
      name: `v${t.version ?? 0}`,
    })) ?? [];

  const handleVersionChange = (version: number) => {
    const selectedVersion = templateVersions?.find(t => t.version === version);
    setCurrentTemplate(selectedVersion);
    setCurrentVersion(version);
  };

  if (fetchTemplateLoading) {
    return (
      <div className="flex h-full w-full justify-center items-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!templatesLibraryEnabled) {
    return (
      <div className="flex h-full w-full justify-center items-center">
        <Typography.H4>
          Editing templates is currently disabled. Please request access in the #growth-eng-external
          channel if needed.
        </Typography.H4>
      </div>
    );
  }

  return (
    <div className="p-8 flex flex-col gap-4 h-full">
      <Link to={Routes.TemplatesLibrary} className="flex gap-3 items-center">
        <Button className="w-fit bg-transparent shadow-none" variant="neutral">
          <BaseIcon
            contrastText
            edge="start"
            icon={['fas', 'arrow-left']}
            variant="neutral"
            size="sm"
          />
          Exit
        </Button>
      </Link>
      <div className="flex flex-row justify-between w-full gap-5 items-center">
        <Typography.H4>{templateId ? `Update Template` : 'Create New Template'}</Typography.H4>
        {templateId && (
          <SelectMenu
            options={versionOptions}
            selectedOptionId={currentVersion}
            setSelected={handleVersionChange}
          />
        )}
      </div>
      <TemplateCreationForm template={currentTemplate} />
    </div>
  );
};

export default TemplateSetupPage;
